import type { FileError, FilesContent } from "component/filesContainer/types";
import type { FilesType } from "pages/pickers/types";

export type PickerFileApiRow = Record<string, unknown>;

export type PickerFileContentInput<T extends string> = {
  tag: T;
  isUpload?: boolean;
  error?: FileError;
} & PickerFileApiRow;

export type NormalizedPickerFileRow<T extends string> = {
  tag: T;
  isUpload: boolean;
  error?: FileError;
};

export const toBase64 = (file: File) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });

export const getBase64FromUrl = async (url: string) => {
  const data = await fetch(url);
  const blob = await data.blob();
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.readAsDataURL(blob);
    reader.onloadend = () => resolve(reader.result);
  });
};

export const isBase64 = (str: string) => {
  if (str === "" || str.trim() === "") return false;
  try {
    window.atob(str);
    return true;
  } catch (e) {
    return false;
  }
};

export const createCSV = (datosExport: any, fileName: string) => {
  var blob = new Blob([datosExport], { type: "text/csv;charset=utf-8;" });
  let Navigator = window.navigator as any;
  if (Navigator.msSaveBlob) {
    Navigator.msSaveBlob(blob, fileName + ".csv");
  } else {
    var link = document.createElement("a");
    if (link.download !== undefined) {
      var url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", fileName + ".csv");
      link.setAttribute("style", "visibility:hidden");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }
};

const PICKER_UPLOAD_FLAG_KEYS = [
  "upload",
  "uploaded",
  "loaded",
  "filled",
  "present",
  "exists",
  "attached",
  "is_upload",
  "isUpload",
] as const;

const PICKER_FILE_URL_KEYS = [
  "url",
  "fileUrl",
  "file_url",
  "downloadUrl",
  "download_url",
  "path",
  "filePath",
] as const;

const PICKER_FILE_ID_KEYS = ["id", "fileId", "documentId"] as const;

const PICKER_STATUS_DONE = [
  "UPLOADED",
  "COMPLETE",
  "COMPLETED",
  "APPROVED",
  "OK",
  "LOADED",
  "SUCCESS",
] as const;

const PICKER_STATUS_PENDING = [
  "EMPTY",
  "MISSING",
  "NONE",
  "PENDING_UPLOAD",
  "NOT_UPLOADED",
] as const;

export const normalizePickerFileTag = (tag: unknown): string => {
  if (tag == null) return "";
  return String(tag).trim().replace(/_/g, "-").toLowerCase();
};

export const doesPickerFileRowIndicateUpload = (
  row: PickerFileApiRow
): boolean => {
  const truthyFlag = (v: unknown) =>
    v === true ||
    v === 1 ||
    (typeof v === "string" &&
      (v.trim().toLowerCase() === "true" || v.trim().toLowerCase() === "1"));

  for (const key of PICKER_UPLOAD_FLAG_KEYS) {
    if (truthyFlag(row[key])) return true;
  }

  for (const key of PICKER_FILE_URL_KEYS) {
    const v = row[key];
    if (typeof v === "string" && v.trim() !== "") return true;
  }

  for (const key of PICKER_FILE_ID_KEYS) {
    const v = row[key];
    if (typeof v === "number" && v > 0) return true;
    if (typeof v === "string" && /^\d+$/.test(v.trim()) && parseInt(v, 10) > 0)
      return true;
  }

  const statusRaw =
    row.status ?? row.fileStatus ?? row.documentStatus ?? row.loadStatus;
  if (typeof statusRaw === "string" && statusRaw.length > 0) {
    const upper = statusRaw.toUpperCase();
    if (PICKER_STATUS_DONE.some((t) => upper.includes(t))) return true;
    if (PICKER_STATUS_PENDING.some((t) => upper.includes(t))) return false;
  }

  if (typeof row.isUpload === "boolean") return row.isUpload;
  if (typeof row.upload === "boolean") return row.upload;

  return false;
};

export const normalizePickerFileRow = <T extends string>(
  row: PickerFileContentInput<T>
): PickerFileContentInput<T> & NormalizedPickerFileRow<T> => {
  const raw = row as PickerFileApiRow;
  const rawTag =
    raw.tag ??
    raw.fileTag ??
    raw.documentTag ??
    raw.document_type ??
    raw.type ??
    row.tag;
  const normalizedTag = normalizePickerFileTag(rawTag) as T;
  const resolvedTag = (normalizedTag || row.tag) as T;

  return {
    ...row,
    tag: resolvedTag,
    isUpload: doesPickerFileRowIndicateUpload({ ...raw, tag: resolvedTag }),
    error: (raw.error as FileError | undefined) ?? "NONE",
  };
};

export const normalizePickerFileSection = <T extends string>(
  section: FilesContent<T> | undefined
): FilesContent<T> => {
  const fallback = section ?? ({ status: "EMPTY", content: [] } as FilesContent<T>);
  const rows = Array.isArray(fallback.content) ? fallback.content : [];
  return {
    ...fallback,
    status: fallback.status ?? "EMPTY",
    content: rows.map((item) =>
      normalizePickerFileRow(item as PickerFileContentInput<T>)
    ),
  };
};

export const normalizeAllPickerFilesFromApi = (files: FilesType): FilesType => {
  return {
    personalData: normalizePickerFileSection(files.personalData),
    accountingData: normalizePickerFileSection(files.accountingData),
    vehicle: normalizePickerFileSection(files.vehicle),
  };
};
